import React, { useEffect, useState } from 'react';
import { Card, Button, Badge } from '@shopify/polaris';
import { Redirect } from '@shopify/app-bridge/actions';
import { useAppBridge } from '@shopify/app-bridge-react';
import { config, DEFAULT_PLAN_NAMES, PLAN_FEATURES } from '../constants';

// Function to get resolved features for a plan
function getResolvedFeatures(plan, planFeatures) {
  return planFeatures[plan] || [];
}

import ThemeEmbedCard from './ThemeEmbedCard';

export default function Summary({ shop, navigate }) {
  const [currentPlan, setCurrentPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const app = useAppBridge();

  useEffect(() => {
    async function fetchPlan() {
      try {
        const res = await fetch(`${config.API_BASE}/current-plan?shop=${shop}`);
        const data = await res.json();
        setCurrentPlan(data?.plan || 'Unknown');
        setFeatures(getResolvedFeatures(data?.plan || 'Free', PLAN_FEATURES));
      } catch {
        setCurrentPlan('Unknown');
        setFeatures(getResolvedFeatures('Free', PLAN_FEATURES));
      } finally {
        setLoading(false);
      }
    }
    if (shop) fetchPlan();
  }, [shop]);

  // --- App Bridge Navigation Handlers ---
  function handleRedirect(path) {
    const redirect = Redirect.create(app);
    redirect.dispatch(Redirect.Action.ADMIN_PATH, `/apps/${config.APP_HANDLE}${path}`);
  }

  return (
    <div style={{ width: '100%', maxWidth: 800, margin: '40px auto', padding: 24, boxSizing: 'border-box', alignSelf: 'center' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Welcome to your App Dashboard! <span role="img" aria-label="wave">👋</span></h1>
      <p style={{ color: '#444', marginBottom: 24 }}>Get started by following the checklist below to set up your store's chat and support experience.</p>
      {/* Theme store app embed card */}
      <div style={{ marginBottom: 20 }}>
        <ThemeEmbedCard shop={shop} />
      </div>

      <Card sectioned style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600, fontSize: 17 }}>
            <span role="img" aria-label="plan">🗂️</span> Current subscription plan: <span style={{ color: '#2a5bd7' }}>{loading ? 'Loading...' : currentPlan}</span>
          </span>
          <Button plain onClick={() => navigate('/subscriptions')}><span role="img" aria-label="settings">⚙️</span> Manage Plan</Button>
        </div>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 }}>
        <Card sectioned>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <b><span role="img" aria-label="faq">❓</span> Set up FAQs</b><br />
              <span style={{ color: '#666' }}>Help your customers with instant answers.</span>
            </div>
            <Button onClick={() => navigate('/admin')} style={{ marginLeft: 16 }}><span role="img" aria-label="faq">📋</span> Go to FAQs</Button>
          </div>
        </Card>
        <Card sectioned>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <b><span role="img" aria-label="chat">💬</span> Explore Live Chat</b>
                {!features.includes('Live chat') && (
                  <Badge status="warning" style={{ marginLeft: 8 }}>
                    <span role="img" aria-label="star">⭐</span> Available on Standard plan
                  </Badge>
                )}
              </div>
              <span style={{ color: '#666', display: 'block', marginTop: 6 }}>Enable live chat to connect with customers in real time.</span>
            </div>
            <Button onClick={() => navigate('/livechat')} style={{ marginLeft: 16 }} disabled={!features.includes('Live chat')}>
              <span role="img" aria-label="chat">💬</span> Go to Live Chat
            </Button>
          </div>
        </Card>
        <Card sectioned>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <b><span role="img" aria-label="bell">🔔</span> Settings for Live Chat</b>
                {!features.includes('Notifications') && (
                  <Badge status="warning" style={{ marginLeft: 8 }}>
                    <span role="img" aria-label="star">⭐</span> Available on Standard plan
                  </Badge>
                )}
              </div>
              <span style={{ color: '#666', display: 'block', marginTop: 6 }}>Configure notifications, store hours, and other settings to stay updated.</span>
            </div>
            <Button
              onClick={() => navigate('/settings')}
              style={{ marginLeft: 16 }}
              disabled={!features.includes('Notifications')}
            >
              <span role="img" aria-label="bell">🔔</span> Manage Settings
            </Button>
          </div>
        </Card>
        <Card sectioned>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <b><span role="img" aria-label="paint">🎨</span> Customize Chat Widget</b>
                {!features.includes('Customization') && (
                  <Badge status="warning" style={{ marginLeft: 8 }}>
                    <span role="img" aria-label="star">⭐</span> Available on Standard plan
                  </Badge>
                )}
              </div>
              <span style={{ color: '#666', display: 'block', marginTop: 6 }}>Personalize the look and feel of your chatbot to match your brand.</span>
            </div>
            <Button onClick={() => navigate('/chatbox')} style={{ marginLeft: 16 }} disabled={!features.includes('Customization')}>
              <span role="img" aria-label="paint">🎨</span> Customize
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
